import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BikeIcon, Users, DollarSign, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import ExchangeRateCard from "@/components/dashboard/ExchangeRateCard";
import ExchangeRateInfoCard from "@/components/dashboard/ExchangeRateInfoCard";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getRentals, getActiveRentals, getConfiguration } from "../integrations/supabase/services";
import { useApiService } from "@/hooks/useApiService";
import { isToday, isYesterday, parseISO, differenceInMinutes } from "date-fns";
import { RentalWithRelations } from "@/types/models";
import TodayRentalHistoryCards from "../components/dashboard/TodayRentalHistoryCards";
import PerformanceBranch from "@/components/dashboard/PerformanceBranch";
import WeeklySalesBranch from "@/components/dashboard/WeeklySalesBranch";
import { getWeeklyAdminStats } from "@/integrations/supabase/report-services";

const Dashboard = () => {
  const { profile } = useAuth();
  const { callService } = useApiService(); // Get callService
  const isSuperAdmin = profile?.role === 'super_admin';

  const { data: activeRentalsData, isLoading: isLoadingActiveRentals } = useQuery({
    queryKey: ['activeDashboardRentals'],
    queryFn: () => callService(getActiveRentals), // Use callService
  });

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 2);
  const yesterdayDate = yesterday.toISOString().split('T')[0];
  
  const { data: allRentalsData, isLoading: isLoadingAllRentals } = useQuery({
    queryKey: ['allDashboardRentals', yesterdayDate],
    queryFn: () => callService(() => getRentals(yesterdayDate)), // Use callService
    refetchInterval: 300000, // Refetch every 5 minute
  });

  const { data: weeklyAdminStats, isLoading: isLoadingWeeklyAdminStats } = useQuery({
    queryKey: ['weeklyAdminStats'],
    queryFn: () => callService(getWeeklyAdminStats),
    enabled: isSuperAdmin,
  });

  const { data: configuration, isLoading: isLoadingConfiguration } = useQuery({
    queryKey: ['activeConfiguration'],
    queryFn: () => callService(getConfiguration),
    enabled: isSuperAdmin,
  });

  const activeRentalsCount = activeRentalsData?.length || 0;

  const completedTodayRentals: RentalWithRelations[] = allRentalsData?.filter(
    (rental: RentalWithRelations) => // Ensure correct type for filter
      rental.status === 'completed' &&
      rental.end_date &&
      isToday(parseISO(rental.start_date))
  ) || [];

  const completedRentalsTodayCount = completedTodayRentals.length;
  
  const completedYesterdayRentals: RentalWithRelations[] = allRentalsData?.filter(
    (rental: RentalWithRelations) =>
      rental.status === 'completed' &&
      rental.end_date &&
      isYesterday(parseISO(rental.start_date))
  ) || [];

  // Calculate stats for yesterday (TODAS LAS SUCURSALES)
  const completedRentalsYesterdayCount = completedYesterdayRentals.length;

  const revenueYesterday = completedYesterdayRentals.reduce(
    (sum, rental) => sum + (rental.total_amount || 0),
    0
  );

  const totalRentalTimeMinutesYesterday = completedYesterdayRentals.reduce(
    (sum, rental) => {
      if (rental.start_date && rental.end_date) {
        return sum + differenceInMinutes(parseISO(rental.end_date), parseISO(rental.start_date));
      }
      return sum;
    },
    0
  );

  const averageRentalTimeMinutesYesterday =
    completedRentalsYesterdayCount > 0
      ? Math.round(totalRentalTimeMinutesYesterday / completedRentalsYesterdayCount)
      : 0;

  const uniqueCustomersYesterdayCount = new Set(
    completedYesterdayRentals.map(rental => rental.customer_id).filter(id => id != null)
  ).size;

  // Calculate stats for today
  const revenueToday = completedTodayRentals.reduce(
    (sum, rental) => sum + (rental.total_amount || 0),
    0
  );

  const defaultAdminPercentage = configuration?.administrator_percentage ?? 75;

  const adminProfitToday = completedTodayRentals.reduce((sum, rental) => {
    const adminPercentage = rental.branch?.administrator_percentage ?? defaultAdminPercentage;
    return sum + ((rental.total_amount || 0) * adminPercentage) / 100;
  }, 0);

  const totalRentalTimeMinutes = completedTodayRentals.reduce(
    (sum, rental) => {
      if (rental.start_date && rental.end_date) {
        return sum + differenceInMinutes(parseISO(rental.end_date), parseISO(rental.start_date));
      }
      return sum;
    },
    0
  );

  const adminRevenueYesterday = completedYesterdayRentals.reduce((sum, rental) => {
    const adminPercentage = rental.branch?.administrator_percentage ?? defaultAdminPercentage;
    return sum + ((rental.total_amount || 0) * adminPercentage) / 100;
  }, 0);

  const averageRentalTimeMinutes =
    completedRentalsTodayCount > 0
      ? Math.round(totalRentalTimeMinutes / completedRentalsTodayCount)
      : 0;

  const uniqueCustomersTodayCount = new Set(
    completedTodayRentals.map(rental => rental.customer_id).filter(id => id != null)
  ).size;

  const formatAverageTime = (totalMinutes: number) => {
    if (isNaN(totalMinutes) || totalMinutes < 0) {
      return "00:00";
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const paddedHours = String(hours).padStart(2, '0');
    const paddedMinutes = String(minutes).padStart(2, '0');
    return `${paddedHours}:${paddedMinutes}`;
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-800">Panel Principal</h1>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          <Link to="/rentals">
            <Card className="block h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Alquileres Activos</CardTitle>
                <BikeIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoadingActiveRentals ? (
                  <div className="text-2xl font-bold">Cargando...</div>
                ) : (
                  <div className="text-2xl font-bold">{activeRentalsCount}</div>
                )}
                {/* <p className="text-xs text-muted-foreground">
                  +2 desde ayer
                </p> */}
              </CardContent>
            </Card>
          </Link>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Clientes</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingAllRentals ? (
                <div className="text-2xl font-bold">Cargando...</div>
              ) : (
                <>
                  <div className="text-2xl font-bold">{uniqueCustomersTodayCount}</div>
                  <p className="text-xs text-muted-foreground">
                    {uniqueCustomersYesterdayCount} atendidos ayer
                  </p>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* {isSuperAdmin && (
            <Card title="Suma de ingresos de todas las sucursales">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ingresos Hoy ({completedRentalsTodayCount})</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoadingAllRentals || isLoadingConfiguration ? (
                  <div className="text-2xl font-bold">Cargando...</div>
                ) : (
                  <>
                    <div className="text-2xl font-bold">${revenueToday.toFixed(2)}</div>
                    <p className="text-xs text-muted-foreground">
                      ${revenueYesterday.toFixed(2)} (ingresos ayer)
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {completedRentalsYesterdayCount} (completados ayer)
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          )} */}

          <Card title={isSuperAdmin ? "Ganancias de hoy" : "Suma de ingresos de la sucursal"}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {isSuperAdmin ? "Ganancia Hoy" : "Ingresos Hoy"} ({completedRentalsTodayCount})
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingAllRentals || isLoadingConfiguration ? (
                 <div className="text-2xl font-bold">Cargando...</div>
              ) : (
                <>
                  <div className="text-2xl font-bold">
                    ${isSuperAdmin ? adminProfitToday.toFixed(2) : revenueToday.toFixed(2)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    ${isSuperAdmin ? adminRevenueYesterday.toFixed(2) : revenueYesterday.toFixed(2)} (ingresos ayer)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {completedRentalsYesterdayCount} (completados ayer)
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {isSuperAdmin && (
            <Card title="Ganancia neta del administrador (excluyendo la comisión de operadores) de la semana en curso">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ingresos Semana</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoadingWeeklyAdminStats ? (
                  <div className="text-2xl font-bold">Cargando...</div>
                ) : (
                  <>
                    <div className="text-2xl font-bold">${weeklyAdminStats?.currentWeekProfit.toFixed(2) ?? "0.00"}</div>
                    <p className="text-xs text-muted-foreground">
                      ${weeklyAdminStats?.previousWeekProfit.toFixed(2) ?? "0.00"} (semana pasada)
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {weeklyAdminStats?.previousWeekRentals ?? 0} (completados la semana pasada)
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          )}
          
          {isSuperAdmin ? <ExchangeRateCard /> : <ExchangeRateInfoCard/> }
          
          {!isSuperAdmin && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tiempo Promedio (Hoy)</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoadingAllRentals ? (
                  <div className="text-2xl font-bold">Cargando...</div>
                ) : (
                  <>
                    <div className="text-2xl font-bold">{formatAverageTime(averageRentalTimeMinutes)}</div>
                    <p className="text-xs text-muted-foreground">
                      {formatAverageTime(averageRentalTimeMinutesYesterday)} promedio ayer
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {!isLoadingAllRentals && (
          <div className="grid gap-4 md:grid-cols-2">
            <WeeklySalesBranch />
            <PerformanceBranch />
            <TodayRentalHistoryCards rentals={completedTodayRentals} />
          </div>
        )}
         {/* Show loading state for the table as well */}
        {isLoadingAllRentals && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Historial de Alquileres de Hoy</h3>
              <p>Cargando historial...</p>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default Dashboard;
